import { useRef, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button, DataTable, Modal, type DataTableColumn } from '@atrvd/saas-ui'
import { formatCurrency } from '@dcp/shared/utils/formatters'
import { useTablePagination } from '@/hooks/useTablePagination'
import { QueryErrorMessage } from '@/components/common/QueryErrorBanner'
import { TransactionNotesCell, TransactionNotesProvider } from './TransactionNotesCell'
import {
  buildPageRows,
  flattenGroups,
  renderOrDash,
  type TxnDialogRow,
} from './viewBankTransactionsRows'

/** A LINE OF CREDIT transaction behind a LOC Usage figure (from `useLocTransactions`). */
export interface BankTxn {
  /** `transactions` row id — the key a Notes edit is saved against. */
  id: string
  /** Transaction `source`; Notes are editable only for the manual-page sources. */
  source: string
  /** Not displayed; kept to tell which facility a row came from when debugging. */
  facilityId: string
  /** Not displayed; the pack of the row's LOC line, null for a standalone line. */
  packId: string | null
  /** Transaction date, displayed as-is (MM.DD.YYYY). */
  date: string
  /** Bank account name, or '' (renders as "—"). */
  account: string
  /** Bank account number, or '' (renders as "—"). */
  accountNumber: string
  /** Signed amount. */
  amount: number
  description: string
  /** Free-form memo, or '' (renders as "—"). */
  memo: string
  /** Free-form notes, or '' (renders as "—"). */
  notes: string
}

/** The transactions of one facility; a pack drill has one group per member. */
export interface BankTxnGroup {
  /**
   * Group identity for heading boundaries (`useLocTransactions` sets the
   * facility id). `facilities.name` is not unique, so two members of one pack
   * may share a name; comparing names would merge their blocks under one
   * heading. Optional for hand-built groups, which fall back to `name`.
   */
  id?: string
  /** Heading shown above the group's transactions. */
  name: string
  transactions: BankTxn[]
}

interface ViewBankTransactionsDialogProps {
  open: boolean
  onClose: () => void
  /** Pack or facility name shown in the modal title. */
  referenceName: string
  groups: BankTxnGroup[]
  /**
   * Starting LOC usage of the drilled scope (one line's, or the sum of a
   * pack's lines). LOC Usage = this + the transactions, so it is shown as the
   * opening row and folded into the Total. Defaults to 0 (no opening row).
   */
  startingBalance?: number
  /**
   * Label each facility block. True for a pack drill — even a one-member pack,
   * since the title names the pack; false where the title already names the
   * facility. Defaults to false.
   */
  showGroupHeadings?: boolean
  /** Shows a "sample data" banner, for a caller that ever renders placeholder rows. */
  isSampleData?: boolean
  /** The feed's first fetch is in flight: skeleton rows and "Total: —". Loses to `errorMessage`. */
  isLoading?: boolean
  /**
   * The feed failed with nothing to show: the message replaces the rows and
   * the Total reads "—". Any string, the empty one included, is an error —
   * PostgREST can report a blank message.
   */
  errorMessage?: string
  /** Refetch of the failed feed, offered as Retry next to `errorMessage`. */
  onRetry: () => void
  /** The feed is refetching (TanStack `isFetching`): Retry reads "Retrying…" and is disabled. */
  retrying: boolean
  /**
   * The LOC Usage figure drilled from, for ONE check: if it differs from
   * `startingBalance`, transactions must exist, so an empty list means they
   * were lost in transit (the 1000-row truncation this guards against). Not a
   * general `total !== expectedTotal` check: this figure comes from the
   * caller's cached query while the list is fresh, so a concurrent write makes
   * them legitimately disagree. `undefined` skips the check.
   */
  expectedTotal?: number
  /**
   * A background refetch failed while the last good rows are still shown:
   * a non-destructive notice above the table, rows and Total unchanged.
   */
  refreshFailed?: boolean
}

/** Float noise on currency math that does not prove a transaction exists. */
const AMOUNT_EPSILON = 0.005

/** Text of a transaction column; the opening row only fills Description and Amount. */
function txnText(pick: (txn: BankTxn) => string) {
  return (row: TxnDialogRow) => (row.kind === 'transaction' ? renderOrDash(pick(row.txn)) : null)
}

const COLUMNS: DataTableColumn<TxnDialogRow>[] = [
  { key: 'date', header: 'Transaction Date', render: txnText((txn) => txn.date) },
  { key: 'account', header: 'Account Name', render: txnText((txn) => txn.account) },
  { key: 'accountNumber', header: 'Account Number', render: txnText((txn) => txn.accountNumber) },
  {
    key: 'amount',
    header: 'Amount',
    render: (row) =>
      row.kind === 'opening'
        ? formatCurrency(row.amount)
        : row.kind === 'transaction'
          ? formatCurrency(row.txn.amount)
          : null,
    cellClassName: (row) => {
      if (row.kind === 'opening')
        return row.amount < 0 ? 'font-medium text-destructive' : 'font-medium'
      return row.kind === 'transaction' && row.txn.amount < 0 ? 'text-destructive' : undefined
    },
  },
  {
    key: 'description',
    header: 'Description',
    // Keeps Description from wrapping word by word next to the fixed-width Notes.
    minWidth: '220px',
    render: (row) =>
      row.kind === 'opening'
        ? 'Starting LOC usage balance'
        : row.kind === 'transaction'
          ? renderOrDash(row.txn.description)
          : null,
    cellClassName: (row) => (row.kind === 'opening' ? 'font-medium' : undefined),
  },
  { key: 'memo', header: 'Memo', render: txnText((txn) => txn.memo) },
  {
    key: 'notes',
    header: 'Notes',
    // Room for the editor (input + Save/Discard) even in view mode, so
    // entering edit mode does not widen the column.
    width: '280px',
    render: (row) => (row.kind === 'transaction' ? <TransactionNotesCell txn={row.txn} /> : null),
    cellClassName: 'align-top',
  },
]

function rowKey(row: TxnDialogRow): string {
  switch (row.kind) {
    case 'opening':
      return 'opening'
    case 'heading':
      return row.key
    case 'transaction':
      return row.txn.id
  }
}

function renderHeading(row: TxnDialogRow) {
  if (row.kind !== 'heading') return null
  return {
    content: (
      <span data-row-kind="heading">
        {row.name}
        {row.continued && (
          <span className="ml-2 font-normal text-muted-foreground">(continued)</span>
        )}
      </span>
    ),
    rowClassName: 'bg-muted/50 hover:bg-muted/50',
    cellClassName: 'text-sm font-semibold text-foreground',
  }
}

/** Full-width rows never reach `rowProps`, so headings carry `data-row-kind` in their content. */
function rowProps(row: TxnDialogRow) {
  return { 'data-row-kind': row.kind }
}

/**
 * Amber notice for "this list is not trustworthy as shown" cases, in the
 * missing-data warning style (shared look with UpdateLocAvailabilityDialog).
 */
function AmberNotice({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  )
}

/**
 * LOC Usage drill-down: the LINE OF CREDIT transactions behind a LOC Usage
 * figure, after an opening row for the starting usage, with the Total
 * (starting usage + every transaction) in the footer.
 *
 * Rows are paged flat across groups with the app-wide sizes (no pager at 20
 * or fewer). The Total stays the GRAND total on every page: it must reconcile
 * with the figure the user drilled from. Notes are inline-editable for
 * manual-page sources ({@link TransactionNotesCell}).
 *
 * The table scrolls with the Modal body (one scroller) and its header pins to
 * the top of it.
 */
export function ViewBankTransactionsDialog({
  open,
  onClose,
  referenceName,
  groups,
  showGroupHeadings = false,
  startingBalance = 0,
  isSampleData = false,
  isLoading = false,
  errorMessage,
  onRetry,
  retrying,
  expectedTotal,
  refreshFailed = false,
}: ViewBankTransactionsDialogProps) {
  const tableRef = useRef<HTMLTableElement>(null)
  const flat = flattenGroups(groups)
  const total = startingBalance + flat.reduce((sum, row) => sum + row.txn.amount, 0)

  // Nothing in flight and nothing failed: the banners and the pager reason
  // about a final feed, so all three share this one gate.
  const feedSettled = !isLoading && errorMessage === undefined

  // The total is withheld until the feed settles, so the pager stays hidden
  // and the page is not clamped against an unsettled list.
  const pagination = useTablePagination({
    totalItems: feedSettled ? flat.length : undefined,
    resetKey: referenceName,
  })
  // A new page starts at its top: the Modal body keeps its scroll position, so
  // page 2 would otherwise open with its first rows under the sticky header.
  // The scroller is the saas-ui Modal body, the only one in the dialog.
  const scrollToTop = () => {
    const scroller = tableRef.current?.closest<HTMLElement>('.overflow-y-auto')
    if (scroller) scroller.scrollTop = 0
  }
  const footer = pagination.dataTablePagination
  const tablePagination = footer && {
    ...footer,
    onPageChange: (page: number) => {
      scrollToTop()
      footer.onPageChange(page)
    },
    onPageSizeChange: (size: number) => {
      scrollToTop()
      footer.onPageSizeChange?.(size)
    },
  }
  const rows = buildPageRows({
    flat,
    offset: pagination.offset,
    pageSize: pagination.pageSize,
    showGroupHeadings,
    startingBalance,
  })

  const missingTransactions =
    feedSettled &&
    flat.length === 0 &&
    expectedTotal !== undefined &&
    Math.abs(expectedTotal - startingBalance) > AMOUNT_EPSILON
      ? { expectedTotal }
      : null

  return (
    <TransactionNotesProvider groups={groups}>
      <Modal
        isOpen={open}
        onClose={onClose}
        title={`LOC Usage for ${referenceName}`}
        size="xl"
        // Wider than the `xl` preset for 7 columns plus the Notes editor.
        className="max-w-6xl"
        footer={
          <div className="flex items-center justify-end gap-4">
            {/* A total of an unsettled feed would be a believable wrong figure. */}
            <span className="font-medium">Total: {feedSettled ? formatCurrency(total) : '—'}</span>
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        }
      >
        {isSampleData && (
          <AmberNotice>
            Sample data — bank transactions are not yet connected. These rows are placeholders and
            do not reflect real activity.
          </AmberNotice>
        )}

        {refreshFailed && feedSettled && (
          <AmberNotice>
            The transaction list could not be refreshed. What&apos;s shown below is the last
            successfully loaded version, which may be out of date.
          </AmberNotice>
        )}

        {missingTransactions && (
          <AmberNotice>
            LOC Usage for {referenceName} is {formatCurrency(missingTransactions.expectedTotal)},
            which differs from its starting balance of {formatCurrency(startingBalance)} — so
            transactions make up the difference, but none could be loaded here. This list is
            incomplete; please reload the page before relying on it.
          </AmberNotice>
        )}

        <DataTable
          columns={COLUMNS}
          data={rows}
          rowKey={rowKey}
          renderFullWidthRow={renderHeading}
          rowProps={rowProps}
          density="compact"
          scroll="parent"
          stickyHeader
          showColumnControls={false}
          itemName="transactions"
          loading={isLoading}
          tableRef={tableRef}
          error={
            errorMessage !== undefined && (
              <QueryErrorMessage
                // A blank message is still a failure; show a generic one for it.
                message={errorMessage || 'Failed to load transactions'}
                onRetry={onRetry}
                retrying={retrying}
              />
            )
          }
          emptyState={
            <p className="text-sm text-muted-foreground">No LOC transactions for this reference.</p>
          }
          pagination={tablePagination}
        />
      </Modal>
    </TransactionNotesProvider>
  )
}
